import { toast } from "react-toastify";
import contactUUID from "./Contacts/contactUUID";
import { AssetType } from "./store/slices/mapsSlice";
import { formatAssetKind } from "./Assets/AssetKind";
import { EventType } from "./store/slices/settingsSlice";

const el: any = document.querySelector("#app");
export const { contactsClientId, apiBase } = JSON.parse(
  el?.dataset?.page ?? ""
).props;

const headersList = {};

export async function proxyAPI({
  prefix,
  url,
  method = "GET",
  body,
  formData,
  request,
  setLoading,
  headers = {},
  hideToasts = false,
}: {
  prefix: "logic/workflows/api" | "contacts/v1" | "logic/reporting";
  url: string;
  method?: "GET" | "POST" | "DELETE" | "PATCH";
  body?: any;
  formData?: any;
  request?: any;
  setLoading?: (isLoading: boolean) => void;
  headers?: Record<string, string>;
  hideToasts?: boolean;
}) {
  let URL = url;
  if (URL[0] === "/") {
    URL = URL.slice(1);
  }
  try {
    setLoading?.(true);
    const response = await fetch(`${apiBase}/${prefix}/${URL}`, {
      method,
      headers: {
        ...headersList,
        ...headers,
        Authorization: `Bearer ${(window as any).apiToken}`,
      },
      // include body for post and delete
      ...(["POST", "DELETE", "PATCH"].includes(method) && body
        ? { body: JSON.stringify(body) }
        : {}),
      ...(formData ? { body: formData } : {}),

      // include request
      ...(request && { signal: request.signal }),
    });
    const data = await response.json();
    if (!response.ok) {
      if (response.status === 403) {
        if (!hideToasts) {
          toast.error(
            "You don't have enough permissions to perform this action."
          );
        }
        setLoading?.(false);
        return null;
      }
      if (response.status === 401) {
        // token not valid - get new token
        if (data.token_expired) {
          const newTokenResponse = await fetch(`/api-gateway/get-new-token`, {
            headers: { Accept: "application/json" },
          });
          // php session expired - login
          if (newTokenResponse.status === 401) {
            const returnUrl = encodeURIComponent(window.location.href);
            window.location.href = `/login?session-expired=true&return=${returnUrl}`;
            return;
          }
          // try again with new token
          const newTokenData = await newTokenResponse.json();
          const res = await fetch(`${apiBase}/${prefix}/${URL}`, {
            method,
            headers: {
              ...headersList,
              ...headers,
              Authorization: `Bearer ${newTokenData.token}`,
            },
            ...(method === "POST" ? { body: JSON.stringify(body) } : {}),
          });
          if (res.ok) {
            (window as any).apiToken = newTokenData.token;
          }
          setLoading?.(false);
          return await res.json();
        }
      }
      if (!hideToasts) {
        toast.error(data.error || data.message || "Something went wrong.", {
          toastId: "error-message",
        });
      }

      setLoading?.(false);
      return null;
    }
    setLoading?.(false);
    return data;
  } catch (error) {
    console.log(error);
    if (!hideToasts) {
      toast.error("Something went wrong.\n" + error, {
        toastId: "error-message",
      });
    }
    setLoading?.(false);
    return null;
  }
}

// returns a funtion to be used in input component for paginated options
export function loadContactSetsPaginated({
  contactSetType,
}: { contactSetType?: "static" | "dynamic" } = {}) {
  return async function (search: string, prevOptions: any) {
    const perPage = 5;

    const data = await proxyAPI({
      prefix: "contacts/v1",
      url: `contact-sets?search_label=${search}&paginate.page=${
        prevOptions.length / perPage
      }&paginate.per_page=${perPage}${
        contactSetType ? `&with_type=${contactSetType}` : ""
      }`,
    });

    return {
      options: data.contactSets.map(
        (cs: { uuid: string; label: string; membersCount: number }) => ({
          value: cs.uuid,
          label: `${cs.label} (${cs.membersCount || 0} members)`,
        })
      ),
      hasMore:
        data.pagination.total > prevOptions.length + data.contactSets.length,
    };
  };
}

export async function loadComponentOptionsPaginated(
  search: string,
  prevOptions: any
) {
  const perPage = 5;

  const data = await proxyAPI({
    prefix: "logic/workflows/api",
    url: `list-projects?limit=${perPage}${
      prevOptions.length ? `&offset=${prevOptions.length}` : ""
    }&status=production&order=created&direction=desc&label=${search}`,
  });

  return {
    options: data.projects.map(
      (cs: { UUID: string; Label: string; Description: string }) => ({
        value: cs.UUID,
        label: `${cs.Label} (${cs.Description || "No description"})`,
      })
    ),
    hasMore: data.total > prevOptions.length + data.projects.length,
  };
}

export async function loadAssetOptionsPaginated(
  search: string,
  prevOptions: any
) {
  const perPage = 5;

  const data = await proxyAPI({
    prefix: "logic/workflows/api",
    url: `assets?search=${search}&page=${
      prevOptions.length / perPage
    }&perPage=${perPage}`,
  });

  return {
    options: (data.assets || []).map((cs: AssetType) => ({
      value: cs.Uuid,
      label: `${cs.Label} - ${formatAssetKind(cs.Kind)} (${
        cs.Stats?.Locations || 0
      } locations)`,
    })),
    hasMore: data.total > prevOptions.length + (data.assets || []).length,
  };
}

export async function loadEventOptionsPaginated(
  search: string,
  prevOptions: any,
  currentUUIDs: string[] = []
) {
  const perPage = 5;

  const data = await proxyAPI({
    prefix: "logic/workflows/api",
    url: `events?search=${search}&page=${
      prevOptions.length / perPage
    }&perPage=${perPage}${
      currentUUIDs.length ? `&or_uuid=${currentUUIDs.join("&or_uuid=")}` : ""
    }`,
  });

  return {
    options: (data.events || []).map((cs: EventType) => ({
      value: cs.Uuid,
      label: `${cs.Label}`,
    })),
    hasMore: data.total > prevOptions.length + (data.events || []).length,
  };
}

export async function loadContactOptionsPaginated(
  search: string,
  prevOptions: any
) {
  const data = await proxyAPI({
    prefix: "contacts/v1",
    url: `find`,
    method: "POST",
    body: {
      filters: [
        {
          values: [search],
          is_or: true,
          fields: [],
          formats: ["card", "primary"],
          order: 0,
          operator: "prefix",
        },
      ],
      paginate: {
        order: "desc",
        order_by: "created_at",
        page: prevOptions.length / 10,
        perPage: 10,
      },
      fields: { uuids: [], cards: true, channels: true },
    },
  });

  return {
    options: data.contacts.map((cs: any) => {
      const value = cs.details.sort(
        (a: any, b: any) => a.field.order - b.field.order
      )[0].values[0];
      return {
        value: cs.uuid,
        label: value?.option?.label ?? value?.data ?? cs.uuid,
        contact: cs,
      };
    }),
    hasMore: data.pagination.total > prevOptions.length + data.contacts.length,
  };
}

export async function loadNylasEventsCalendarOptionsPaginated(
  search: string,
  prevOptions: any
) {
  const perPage = 5;

  const data = await proxyAPI({
    prefix: "logic/workflows/api",
    url: `booking-api-connections?search=${search}&offset=${prevOptions.length}&limit=${perPage}`,
  });

  return {
    options: data.connections.map((cs: any) => ({
      value: cs.Uuid,
      label: `${cs.Label} - ${cs.Kind} `,
    })),
    hasMore: data.total > prevOptions.length + data.connections.length,
  };
}

export async function loadApiProfilesPaginated(
  search: string,
  prevOptions: any
) {
  const perPage = 5;

  const data = await proxyAPI({
    prefix: "logic/workflows/api",
    url: `api-profiles?search=${search}&offset=${prevOptions.length}&limit=${perPage}`,
  });

  return {
    options: data.profiles.map((cs: any) => ({
      value: cs.Uuid,
      label: `${cs.Label} - (BaseUrl: "${cs.BaseUrl}")`,
    })),
    hasMore: data.total > prevOptions.length + data.profiles.length,
  };
}

export async function createCS(
  label = "New Group",
  uuid = contactUUID(),
  callBack = () => {}
) {
  const newCS = {
    contact_set: {
      uuid,
      filters: [],
      type: "static",
      label,
    },
  };

  const data = await proxyAPI({
    prefix: `contacts/v1`,
    url: `contact-set`,
    method: "POST",
    body: newCS,
  });

  if (data.message === "OK") {
    toast.success("New group created successfully");
    callBack?.();
  }
}
